import { z } from 'zod';
import type { HttpService } from '@web/data/services';

import { CustomerHitFieldsSchema } from '../search/customer_search_repository';

/** A traveler the operator can view as: the lean CRM hit plus their open cards. */
const ViewAsHitSchema = CustomerHitFieldsSchema.extend({
  openCards: z.array(z.object({ id: z.string(), seq: z.number() })),
});
export type ViewAsHit = z.infer<typeof ViewAsHitSchema>;

const ViewAsSessionSchema = z.object({
  grantId: z.string(),
  token: z.string(),
  expiresAt: z.coerce.date(),
  /** `false` = the traveler has no app binding: the console says so. Absent = treated as on the app. */
  travelerOnApp: z.boolean().optional(),
});
/** A granted view: the token lives only in component state, never in the URL or storage. */
export type ViewAsSession = z.infer<typeof ViewAsSessionSchema>;

interface ViewAsOpenInput {
  customerId: string;
  justification: string;
  cardId?: string;
}

export interface ViewAsRepository {
  search: (orgId: string, q: string) => Promise<ViewAsHit[]>;
  /** Throws `HttpError` 409 (`traveler_not_found`) when the traveler does not exist. */
  open: (orgId: string, input: ViewAsOpenInput) => Promise<ViewAsSession>;
  close: (orgId: string, grantId: string) => Promise<void>;
}

/** kanban-api `/orgs/:orgId/traveler-views` — "ver como viajante" grants. */
export function createViewAsRepository(http: HttpService): ViewAsRepository {
  const base = (orgId: string): string => `/orgs/${encodeURIComponent(orgId)}/traveler-views`;
  return {
    search: (orgId, q) =>
      http.get(`${base(orgId)}/search`, {
        query: { q, limit: 20 },
        schema: z.array(ViewAsHitSchema),
      }),
    open: (orgId, input) => http.post(base(orgId), input, { schema: ViewAsSessionSchema }),
    close: async (orgId, grantId) => {
      await http.post(`${base(orgId)}/${encodeURIComponent(grantId)}/close`);
    },
  };
}
